import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Lock, Plus, Search, Trash2 } from "react-feather";
import { C, tbl } from "../../reporting/reportingStyles";
import { RCard, SectionTitle, Badge, TablePagination, usePagination, Loader } from "../../reporting/ReportingShared";
import {
  addMenuHiddenProducts,
  getCategoryListing,
  getMenuHiddenProducts,
  getProductListing,
  removeMenuHiddenProduct,
} from "../../../utils/apiCalls";
import { getProductImageUrl } from "../../../utils/imageUtils";

/**
 * Products hidden in one menu.
 *
 * Two sources write here, and the table keeps them apart:
 *  - "merchant": hidden in the merchant's POS menu and written by the menu sync. Read-only here,
 *    because the next sync would put it straight back.
 *  - "internal": hidden from this panel. The sync never touches these.
 *
 * A product can be hidden by both; it is one row with both badges, and removing ours leaves it
 * hidden until the POS un-hides it too. Adds and removes save immediately — they are separate
 * records from the menu form and do not wait for its Save button.
 */
const SOURCE_LABELS = { merchant: "POS", internal: "Internal" };

const SOURCE_FILTERS = [
  { key: "all", label: "All" },
  { key: "merchant", label: "POS" },
  { key: "internal", label: "Internal" },
];

const formatPrice = (price) => (price === null || price === undefined ? "—" : Number(price).toFixed(2));

// One entry per product, with every source that hides it.
export const groupHiddenRows = (rows) => {
  const byProduct = new Map();
  for (const row of rows || []) {
    if (!row.product) continue;
    const entry = byProduct.get(row.productId) || { product: row.product, sources: [] };
    entry.sources.push(row.source);
    byProduct.set(row.productId, entry);
  }
  return [...byProduct.values()].sort((a, b) => (a.product.title || "").localeCompare(b.product.title || ""));
};

const SourceBadge = ({ source }) =>
  source === "merchant" ? (
    <Badge color={C.orange} bg={C.orangeLight}>{SOURCE_LABELS.merchant}</Badge>
  ) : (
    <Badge color={C.coral} bg={C.coralLight}>{SOURCE_LABELS.internal}</Badge>
  );

const ProductCell = ({ product }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 180 }}>
    <img
      src={getProductImageUrl(product)}
      alt=""
      style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", border: `1px solid ${C.border}`, flexShrink: 0 }}
    />
    <div>
      <div style={{ fontWeight: 600, color: C.text }}>{product.title}</div>
      <div style={{ fontSize: 11, color: C.textLight }}>#{product.id}</div>
    </div>
  </div>
);

const inputStyle = {
  padding: "9px 12px",
  fontSize: 13,
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontFamily: "inherit",
  outline: "none",
  color: C.text,
  background: C.white,
  width: "100%",
};

const primaryBtnStyle = {
  background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
  border: "none",
  borderRadius: 8,
  padding: "9px 18px",
  fontWeight: 600,
  color: C.white,
  boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
  cursor: "pointer",
  fontSize: 13,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
};

const ghostBtnStyle = {
  background: C.white,
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  padding: "9px 18px",
  fontWeight: 600,
  color: C.text,
  cursor: "pointer",
  fontSize: 13,
};

const removeBtnStyle = {
  background: C.white,
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  padding: "6px 12px",
  fontWeight: 600,
  color: "#DC2626",
  cursor: "pointer",
  fontSize: 12,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const searchBox = (value, onChange, placeholder) => (
  <div style={{ position: "relative", flex: "1 1 220px" }}>
    <Search size={14} color={C.textLight} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...inputStyle, paddingLeft: 32 }}
    />
  </div>
);

const MenuHiddenProducts = ({ menuId, merchantId, onCountChange }) => {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const applyRows = useCallback(
    (next) => {
      setRows(next);
      onCountChange?.(groupHiddenRows(next).length);
    },
    [onCountChange]
  );

  useEffect(() => {
    let cancelled = false;
    getMenuHiddenProducts(menuId).then((next) => {
      if (!cancelled) applyRows(next || []);
    });
    return () => {
      cancelled = true;
    };
  }, [menuId, applyRows]);

  const grouped = useMemo(() => groupHiddenRows(rows), [rows]);
  const counts = useMemo(
    () => ({
      merchant: grouped.filter((g) => g.sources.includes("merchant")).length,
      internal: grouped.filter((g) => g.sources.includes("internal")).length,
    }),
    [grouped]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return grouped.filter(
      (g) =>
        (sourceFilter === "all" || g.sources.includes(sourceFilter)) &&
        (!term || (g.product.title || "").toLowerCase().includes(term) || String(g.product.id) === term)
    );
  }, [grouped, search, sourceFilter]);

  const { paged, page, limit, setPage, setLimit, total } = usePagination(visible, 10);
  const hiddenIds = useMemo(() => new Set(grouped.map((g) => g.product.id)), [grouped]);

  const confirmRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    const next = await removeMenuHiddenProduct(menuId, pendingRemove.product.id);
    setRemoving(false);
    if (next) applyRows(next);
    setPendingRemove(null);
  };

  return (
    <RCard>
      <SectionTitle
        right={
          <button type="button" style={primaryBtnStyle} onClick={() => setPickerOpen(true)}>
            <Plus size={14} /> Add products
          </button>
        }
      >
        Unavailable Products
      </SectionTitle>

      <div style={{ fontSize: 12, color: C.textMed, marginBottom: 14, lineHeight: 1.5 }}>
        These products are hidden in this menu. <b>POS</b> rows come from the merchant&apos;s POS menu and update on every
        menu sync, so change them in the POS. <b>Internal</b> rows are added here and stay hidden through every sync.
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        {searchBox(search, setSearch, "Search hidden products...")}
        <div style={{ display: "flex", gap: 4, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 3 }}>
          {SOURCE_FILTERS.map((f) => {
            const active = sourceFilter === f.key;
            const count = f.key === "all" ? grouped.length : counts[f.key];
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setSourceFilter(f.key)}
                style={{
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  background: active ? C.white : "transparent",
                  color: active ? C.coral : C.textMed,
                  boxShadow: active ? C.shadow : "none",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {f.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {rows === null ? (
        <Loader />
      ) : (
        <>
          <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
            <table style={tbl.table}>
              <thead>
                <tr>
                  <th style={tbl.th}>Product</th>
                  <th style={tbl.th}>Category</th>
                  <th style={{ ...tbl.th, textAlign: "right" }}>Price</th>
                  <th style={tbl.th}>Status</th>
                  <th style={tbl.th}>Hidden by</th>
                  <th style={{ ...tbl.th, textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(({ product, sources }) => (
                  <tr key={product.id} className="rpt-row">
                    <td style={tbl.td}>
                      <ProductCell product={product} />
                    </td>
                    <td style={{ ...tbl.td, color: C.textMed }}>{product.Category?.name || "—"}</td>
                    <td style={{ ...tbl.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatPrice(product.price)}</td>
                    <td style={tbl.td}>
                      {product.is_active ? (
                        <Badge color={C.green} bg={C.greenLight}>Active</Badge>
                      ) : (
                        <Badge color={C.textMed} bg={C.borderLight}>Inactive</Badge>
                      )}
                    </td>
                    <td style={tbl.td}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {sources.map((s) => (
                          <SourceBadge key={s} source={s} />
                        ))}
                      </div>
                    </td>
                    <td style={{ ...tbl.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {sources.includes("internal") ? (
                        <button type="button" style={removeBtnStyle} onClick={() => setPendingRemove({ product, sources })}>
                          <Trash2 size={13} /> Remove
                        </button>
                      ) : (
                        <span
                          title="Hidden in the merchant's POS menu. Change it there; the next menu sync brings it here."
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textLight }}
                        >
                          <Lock size={12} /> Set in POS menu
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {paged.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                      {grouped.length === 0
                        ? "No products are hidden in this menu."
                        : "No hidden products match these filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <TablePagination total={total} page={page} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
          )}
        </>
      )}

      <AddHiddenProductsModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        menuId={menuId}
        merchantId={merchantId}
        hiddenIds={hiddenIds}
        onSaved={(next) => {
          applyRows(next);
          setPickerOpen(false);
        }}
      />

      <Modal isOpen={!!pendingRemove} toggle={() => !removing && setPendingRemove(null)} centered>
        <ModalHeader toggle={() => !removing && setPendingRemove(null)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Show product in menu?</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24, color: C.textMed, fontSize: 13 }}>
          <b style={{ color: C.text }}>{pendingRemove?.product.title}</b> will no longer be hidden by us.
          {pendingRemove?.sources.includes("merchant") && (
            <div style={{ marginTop: 10, color: C.orange }}>
              The POS menu also hides it, so it stays hidden until it is un-hidden in the POS.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button type="button" style={ghostBtnStyle} disabled={removing} onClick={() => setPendingRemove(null)}>
            Cancel
          </button>
          <button type="button" style={primaryBtnStyle} disabled={removing} onClick={confirmRemove}>
            {removing ? "Removing..." : "Remove"}
          </button>
        </ModalFooter>
      </Modal>
    </RCard>
  );
};

const PICKER_PAGE_SIZE = 10;

// Server-side paged product picker. Products already hidden (by either source) are shown but
// cannot be picked.
const AddHiddenProductsModal = ({ isOpen, onClose, menuId, merchantId, hiddenIds, onSaved }) => {
  const [categories, setCategories] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PICKER_PAGE_SIZE);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Map());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Map());
    setSearchInput("");
    setSearch("");
    setCategoryId("");
    setPage(1);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !merchantId) return;
    getCategoryListing(merchantId).then((list) => setCategories(Array.isArray(list) ? list : []));
  }, [isOpen, merchantId]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!isOpen || !merchantId) return;
    let cancelled = false;
    setLoading(true);
    getProductListing(merchantId, { search, categoryId }, page, limit).then((res) => {
      if (cancelled) return;
      setProducts(res?.data || []);
      setTotalCount(res?.totalCount || 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, merchantId, search, categoryId, page, limit]);

  const pickable = products.filter((p) => !hiddenIds.has(p.id));
  const allPicked = pickable.length > 0 && pickable.every((p) => selected.has(p.id));

  const toggle = (product) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.set(product.id, product);
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      for (const p of pickable) {
        if (allPicked) next.delete(p.id);
        else next.set(p.id, p);
      }
      return next;
    });

  const save = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    const next = await addMenuHiddenProducts(menuId, [...selected.keys()]);
    setSaving(false);
    if (next) onSaved(next);
  };

  return (
    <Modal isOpen={isOpen} toggle={() => !saving && onClose()} size="lg" centered>
      <ModalHeader toggle={() => !saving && onClose()} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
        <span style={{ fontWeight: 700, color: C.text }}>Hide products in this menu</span>
      </ModalHeader>
      <ModalBody style={{ padding: 16 }} className="rpt-wrap">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {searchBox(searchInput, setSearchInput, "Search products...")}
          <select
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
            style={{ ...inputStyle, flex: "0 1 220px", cursor: "pointer" }}
          >
            <option value="">All categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ maxHeight: 420, overflow: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
          {loading ? (
            <Loader />
          ) : (
            <table style={tbl.table}>
              <thead>
                <tr>
                  <th style={{ ...tbl.th, width: 44, textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={allPicked}
                      disabled={pickable.length === 0}
                      onChange={togglePage}
                      title="Select all on this page"
                    />
                  </th>
                  <th style={tbl.th}>Product</th>
                  <th style={tbl.th}>Category</th>
                  <th style={{ ...tbl.th, textAlign: "right" }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const alreadyHidden = hiddenIds.has(product.id);
                  return (
                    <tr
                      key={product.id}
                      className="rpt-row"
                      onClick={() => !alreadyHidden && toggle(product)}
                      style={{ cursor: alreadyHidden ? "default" : "pointer", opacity: alreadyHidden ? 0.55 : 1 }}
                    >
                      <td style={{ ...tbl.td, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={alreadyHidden || selected.has(product.id)}
                          disabled={alreadyHidden}
                          onChange={() => toggle(product)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td style={tbl.td}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <ProductCell product={product} />
                          {alreadyHidden && <Badge color={C.textMed} bg={C.borderLight}>Already hidden</Badge>}
                        </div>
                      </td>
                      <td style={{ ...tbl.td, color: C.textMed }}>{product.Category?.name || "—"}</td>
                      <td style={{ ...tbl.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatPrice(product.price)}</td>
                    </tr>
                  );
                })}
                {products.length === 0 && (
                  <tr>
                    <td colSpan={4} style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalCount > 0 && (
          <TablePagination total={totalCount} page={page} limit={limit} onPageChange={setPage} onLimitChange={setLimit} />
        )}
      </ModalBody>
      <ModalFooter style={{ justifyContent: "space-between" }}>
        <span style={{ fontSize: 12, color: C.textMed }}>
          {selected.size === 0 ? "No products selected" : `${selected.size} selected`}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" style={ghostBtnStyle} disabled={saving} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            style={{ ...primaryBtnStyle, opacity: selected.size === 0 || saving ? 0.6 : 1 }}
            disabled={selected.size === 0 || saving}
            onClick={save}
          >
            {saving
              ? "Saving..."
              : selected.size === 0
                ? "Hide products"
                : `Hide ${selected.size} product${selected.size === 1 ? "" : "s"}`}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default MenuHiddenProducts;
